import { useState, useEffect } from 'react';
import { Plus, X, AlertCircle, CheckCircle, Loader, Info } from 'lucide-react';
import { API_BASE, fetchApi, getAuthHeaders } from '../../services/apiClient';

/** system_configuration key holding the stoplist (semicolon-separated). */
const CONFIG_KEY = 'module_link_stoplist';

/** Seed values offered when no config row exists yet — mirrors the built-in
 *  fallback in rust-backend doc_schema_linker.rs (MODULE_LINK_STOPLIST). */
const DEFAULT_STOPLIST = [
  'State',
  'Reference',
  'Settings (Drop Down menu,Chart Color)',
  'Main Page',
  'Variable',
  'Import Excel',
];

export default function ModuleLinkStoplistEditor() {
  const [names, setNames] = useState<string[]>([]);
  const [keyExists, setKeyExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadStoplist();
  }, []);

  const loadStoplist = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/admin/system-config/${CONFIG_KEY}`, {
        method: 'GET',
        headers,
      });
      if (response.status === 404) {
        // No row yet — offer the built-in defaults; Save will create the row.
        setKeyExists(false);
        setNames(DEFAULT_STOPLIST);
        return;
      }
      if (!response.ok) throw new Error(`Failed to load stoplist: ${response.statusText}`);
      const data = await response.json();
      setKeyExists(true);
      setNames(
        String(data?.config_value ?? '')
          .split(';')
          .map((s: string) => s.trim())
          .filter((s: string) => s.length > 0),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    if (name.includes(';')) {
      setError('Module names cannot contain ";" (it is the list separator)');
      return;
    }
    if (names.some(n => n.toLowerCase() === name.toLowerCase())) {
      setError(`"${name}" is already in the stoplist`);
      return;
    }
    setError('');
    setNames([...names, name]);
    setNewName('');
    setDirty(true);
  };

  const handleRemove = (name: string) => {
    setNames(names.filter(n => n !== name));
    setDirty(true);
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const headers = await getAuthHeaders();
      const value = names.join(';');
      const response = keyExists
        ? await fetchApi(`${API_BASE}/admin/system-config/${CONFIG_KEY}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ config_value: value }),
          })
        : await fetchApi(`${API_BASE}/admin/system-config`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              config_key: CONFIG_KEY,
              config_value: value,
              config_type: 'string',
              description:
                'Module names excluded from document linking (semicolon-separated, case-insensitive). Applied at the start of each document-linking run.',
            }),
          });
      if (!response.ok) throw new Error(`Failed to save stoplist: ${response.statusText}`);
      setKeyExists(true);
      setDirty(false);
      setSuccessMessage('Stoplist saved — applies on the next document-linking run');
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Module Stoplist</h2>
        <p className="text-sm text-gray-500 mt-1">
          Module names excluded in two places: document linking (they match nearly
          every document — common English/UI words) and the Detected Modules of
          Issue Analysis. Chat and all other screens are unaffected.
        </p>
      </div>

      <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Info size={20} className="text-blue-600 flex-shrink-0" />
        <p className="text-sm text-blue-800">
          Changes apply at the start of the <strong>next document-linking run</strong> and
          the <strong>next ticket Analyze</strong> — no restart or redeploy needed. Already
          analyzed tickets keep their stored modules until re-analyzed. Matching is
          case-insensitive.
          {!keyExists && !loading && (
            <> No stoplist is stored yet — the list below shows the built-in default;
            click Save to store it.</>
          )}
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="flex gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader size={24} className="animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
          {/* Current list */}
          <div className="flex flex-wrap gap-2">
            {names.length === 0 && (
              <p className="text-sm text-gray-500 italic">
                Stoplist is empty — no modules are excluded from document linking.
              </p>
            )}
            {names.map(name => (
              <span
                key={name}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-200"
              >
                {name}
                <button
                  onClick={() => handleRemove(name)}
                  className="text-blue-400 hover:text-red-600"
                  aria-label={`Remove ${name}`}
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>

          {/* Add */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={e => {
                setNewName(e.target.value);
                setError('');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder="Add a module name to exclude…"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleAdd}
              disabled={!newName.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
            >
              <Plus size={18} />
              Add
            </button>
          </div>

          {/* Save */}
          <div className="flex justify-end pt-2 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving || (!dirty && keyExists)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
